/**
 * Supporting Documents attachment rows + nav count (#1534).
 *
 * The sidebar nav count MUST equal the number the step's own
 * `All Attachments (N)` header shows, or the user reads "Supporting
 * Documents (12)" in the nav, opens the step and finds eight rows. The only
 * way to guarantee that is for both to come out of the same function, so
 * `buildSupportingDocumentFlatRows` is the single row builder:
 * `SupportingDocuments` renders its result, the sidebar counts it.
 *
 * Scope semantics are therefore not re-derived here — they are whatever
 * `filterInlineAttachmentsFromSectionMap` +
 * `shouldIncludeAttachmentSectionInScope` already do for the step:
 * rich-text inline images are excluded (they are not uploaded documents), and
 * sections belonging to a step that is not in the request's persisted scope are
 * excluded (#8070).
 */

import {
  coerceSectionAttachmentList,
  resolveAttachmentScopeOptions,
  resolveStoredStepIdsForAttachments,
  type AttachmentScopeOptions,
} from './attachmentHelpers';
import { shouldIncludeAttachmentSectionInScope } from './facilityPowerAttachmentGrouping';
import {
  formatAttachmentSectionPathDisplay,
  getStageFromSection,
  SECTION_IDS,
  STAGE_ORDER,
  type Stage,
} from '../constants/sectionIds';
import { filterInlineAttachmentsFromSectionMap } from '../services/attachmentInlineFilter';

/**
 * Type-only, therefore erased at compile time: this module must never hold a
 * runtime reference to `attachmentService` (see `attachmentInlineFilter`).
 */
import type { SavedAttachment } from '../services/attachmentService';

export { formatSupportingDocumentsNavLabel } from './supportingDocumentsNavLabel';

/** A flat Supporting Documents row — a saved attachment plus its display path. */
export interface SupportingDocumentRow extends SavedAttachment {
  _sectionLabel?: string;
}

/**
 * The flat row set Supporting Documents renders, in display order.
 *
 * `listAttachmentsBySection` is expected to be already inline-filtered (the
 * step memoizes that separately because its grouped view needs the same map);
 * {@link countSupportingDocumentAttachments} runs the filter itself.
 */
export function buildSupportingDocumentFlatRows(
  listAttachmentsBySection: Readonly<Record<string, SavedAttachment[]>>,
  options: AttachmentScopeOptions,
): SupportingDocumentRow[] {
  const combined: SupportingDocumentRow[] = [];

  Object.entries(listAttachmentsBySection).forEach(
    ([sectionId, attachments]) => {
      if (!shouldIncludeAttachmentSectionInScope(sectionId, options)) {
        return;
      }

      coerceSectionAttachmentList(attachments).forEach(attachment => {
        if (attachment == null) {
          return;
        }
        combined.push({
          ...attachment,
          _sectionLabel: formatAttachmentSectionPathDisplay(
            attachment.requestSection ?? sectionId,
          ),
        });
      });
    },
  );

  return combined.sort((a, b) => {
    const sectionA = a.requestSection || SECTION_IDS.SUPPORTING_DOCUMENTS;
    const sectionB = b.requestSection || SECTION_IDS.SUPPORTING_DOCUMENTS;
    const stageA = getStageFromSection(sectionA) || 'supporting-documents';
    const stageB = getStageFromSection(sectionB) || 'supporting-documents';

    const orderA = STAGE_ORDER.indexOf(stageA as Stage);
    const orderB = STAGE_ORDER.indexOf(stageB as Stage);

    if (orderA === orderB) {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    }

    return orderA - orderB;
  });
}

export interface SupportingDocumentCountInput {
  /** Raw per-section map, exactly as handed to the step (fixtures included). */
  attachmentsBySection?: Readonly<Record<string, SavedAttachment[]>> | null;
  /** `RequestScopeContext.storedStepIds` — authoritative once registered. */
  requestScopeStoredStepIds?: string[] | null;
  /** `request.enabledStepIds` — fallback before RequestScope registers. */
  requestEnabledStepIds?: string[] | null;
  requestStage?: string | null;
}

/**
 * Total documents on the request, as Supporting Documents counts them.
 *
 * Staged-but-not-yet-uploaded files (`selectedFiles`) are deliberately NOT
 * included: they are not attachments on the request yet, the step's own
 * `All Attachments (N)` header does not count them, and the upload control
 * already lists them.
 */
export function countSupportingDocumentAttachments(
  input: SupportingDocumentCountInput,
): number {
  const { attachmentsBySection } = input;
  if (attachmentsBySection == null) {
    return 0;
  }

  const coerced: Record<string, SavedAttachment[]> = {};
  Object.entries(attachmentsBySection).forEach(([sectionId, attachments]) => {
    coerced[sectionId] = coerceSectionAttachmentList(attachments);
  });

  const scopeOptions = resolveAttachmentScopeOptions({
    storedStepIds: resolveStoredStepIdsForAttachments({
      requestScopeStoredStepIds: input.requestScopeStoredStepIds,
      requestEnabledStepIds: input.requestEnabledStepIds,
    }),
    requestStage: input.requestStage ?? '',
  });

  return buildSupportingDocumentFlatRows(
    filterInlineAttachmentsFromSectionMap(coerced),
    scopeOptions,
  ).length;
}
